import { create } from '@bufbuild/protobuf'

import { AnalysisQuote, AnalysisQuoteSchema } from '@/gen/controls/v1/analysis_result_pb'
import { SignalDetail } from '@/gen/inventory/v1/signal_detail_pb'

import { ANALYSIS_QUOTES_LABELS } from './labels'
import type { QuoteGroup } from './types'

export const groupQuotes = (
  quotes: AnalysisQuote[],
  groupBy?: (quote: AnalysisQuote) => string | undefined,
): QuoteGroup[] => {
  const groups = new Map<string, QuoteGroup>()
  quotes.forEach((quote, index) => {
    const key = groupBy?.(quote) ?? `quote-${index}`
    const group = groups.get(key)
    if (group) {
      group.quotes.push(quote)
    } else {
      groups.set(key, { key, first: quote, quotes: [quote] })
    }
  })
  return Array.from(groups.values())
}

/**
 * Reads a signal's evidence. Live reads always populate `quotes`; only
 * AssessmentSignal snapshots finalized before the AnalysisQuote move carry the
 * deprecated `markdown` and `documentQuotes` fields instead.
 */
export const signalDetailQuotes = (detail?: SignalDetail): AnalysisQuote[] => {
  if (!detail) return []
  if (detail.quotes.length > 0) return detail.quotes

  const legacyMarkdown = detail.markdown
    ? [create(AnalysisQuoteSchema, { quote: { case: 'markdown', value: detail.markdown } })]
    : []
  const legacyDocuments = detail.documentQuotes.map((documentQuote) =>
    create(AnalysisQuoteSchema, { quote: { case: 'documentQuote', value: documentQuote } }),
  )
  return [...legacyMarkdown, ...legacyDocuments]
}

export const analysisQuoteTitle = (quote: AnalysisQuote): string => {
  switch (quote.quote.case) {
    case 'documentQuote':
      return quote.quote.value.name || ANALYSIS_QUOTES_LABELS.documentFallbackTitle
    case 'markdown':
      return quote.title || ANALYSIS_QUOTES_LABELS.markdownFallbackTitle
    default:
      return ANALYSIS_QUOTES_LABELS.markdownFallbackTitle
  }
}

/** Stable analytics value for an evidence quote's kind (decoupled from proto field names). */
export const evidenceTypeAnalyticsValue = (quote: AnalysisQuote): string => {
  switch (quote.quote.case) {
    case 'documentQuote':
      return 'document'
    case 'markdown':
      return 'markdown'
    default:
      return 'unknown'
  }
}
